<template>
  <v-container class="uploader">
    <div class="body" :class="{ 'with-footer': value }">
      <drag-file-input @upload="handleUpload" />
      <v-row class="mx-2">
        <v-col v-for="fileDescriptor in uploadingQueue" :key="fileDescriptor.id" cols="4">
          <preloader
            :name="fileDescriptor.file.name"
            :error="fileDescriptor.error"
            @retry="handleUploadRetry(fileDescriptor.id)"
            @del="deleteUploadEntry(fileDescriptor.id)"
          />
        </v-col>
      </v-row>
      <v-divider />
      <v-row class="mx-2">
        <template v-if="$apollo.queries.files.loading">
          <v-col v-for="index in perPage" :key="index" :cols="small ? 6 : 3">
            <v-skeleton-loader type="image" />
          </v-col>
        </template>
        <template v-else>
          <v-col v-for="image in paginatedFiles" :key="image.key" :cols="small ? 6 : 3">
            <image-preview
              :id="image.key"
              :name="image.name"
              :url="image.url"
              :active="checkSelection(image.key)"
              @select="handleSelection(image)"
              @delete="deleteFile(image.key)"
            />
          </v-col>
        </template>
      </v-row>
      <v-pagination v-model="page" class="mx-5" :length="pagesCount" />
    </div>
    <div v-if="value" class="footer">
      <v-btn color="tertiary" dark @click="clearSelection">
        Clear
      </v-btn>
      <v-btn color="primary" @click="$emit('close')">
        Select
      </v-btn>
    </div>
  </v-container>
</template>

<script>
  import Vue from 'vue'
  import { v4 as UUIDv4 } from 'uuid'
  import { sortBy, pick } from 'lodash-es'
  import DragFileInput from '~/components/core/ImageUploader/components/DragFileInput'
  import Preloader from '~/components/core/ImageUploader/components/Preloader'
  import ImagePreview from '~/components/core/ImageUploader/components/ImagePreview'
  import { getFiles, uploadImage, deleteFile } from '~/graphql/files'

  const PER_PAGE = 16
  const PER_PAGE_SMALL = 8

  export default {
    name: 'ImageUploader',
    components: {
      ImagePreview,
      Preloader,
      DragFileInput
    },
    model: {
      event: 'change'
    },
    props: {
      bucket: {
        type: String,
        required: true
      },
      small: {
        type: Boolean
      },
      value: {
        type: [Array, Object],
        default: null
      }
    },
    apollo: {
      files: {
        query: getFiles,
        variables () {
          return { bucket: this.bucket }
        },
        update: query => sortBy(query.files, 'lastmodified').reverse()
      }
    },
    data () {
      return {
        page: 1,
        uploadingQueue: []
      }
    },
    computed: {
      paginatedFiles () {
        return this.files?.slice((this.page - 1) * this.perPage, this.page * this.perPage) ?? []
      },
      perPage () {
        return this.small ? PER_PAGE_SMALL : PER_PAGE
      },
      pagesCount () {
        return Math.ceil((this.files?.length ?? 0) / this.perPage)
      }
    },
    watch: {
      bucket () {
        this.page = 1
      }
    },
    methods: {
      deleteUploadEntry (id) {
        const index = this.uploadingQueue.findIndex(descriptor => descriptor.id === id)

        if (index >= 0) {
          this.uploadingQueue.splice(index, 1)
        }
      },
      // TODO: add non-image files support
      upload (descriptor) {
        this.$apollo.mutate({
          mutation: uploadImage,
          variables: {
            bucket: this.bucket,
            file: descriptor.file
          },
          update: (store, { data: { imageUpload } }) => {
            const query = { query: getFiles, variables: { bucket: this.bucket } }
            const cache = store.readQuery(query)
            cache.files.unshift(imageUpload)
            store.writeQuery({ ...query, data: cache })
          }
        })
            .then(() => {
              this.deleteUploadEntry(descriptor.id)
            })
            .catch(() => {
              descriptor.error = true
            })
      },
      handleUpload (files) {
        Vue.set(
            this,
            'uploadingQueue',
            this.uploadingQueue.concat(
                files.map((file) => {
                  const descriptor = {
                    file,
                    id: UUIDv4(),
                    error: false
                  }

                  this.upload(descriptor)

                  return descriptor
                })
            )
        )
      },
      handleUploadRetry (id) {
        const descriptor = this.uploadingQueue.find(descriptor => descriptor.id === id)
        if (!descriptor) {
          return null
        }

        descriptor.error = false
        this.upload(descriptor)
      },
      deleteFile (key) {
        this.$apollo.mutate({
          mutation: deleteFile,
          variables: {
            bucket: this.bucket,
            key
          },
          update: (store) => {
            const query = { query: getFiles, variables: { bucket: this.bucket } }
            const cache = store.readQuery(query)
            const index = cache.files.findIndex(file => file.key === key)
            if (index >= 0) {
              cache.files.splice(index, 1)
              store.writeQuery({ ...query, data: cache })
            }
          },
          optimisticResponse: {
            __typename: 'Mutation',
            deleteFile: {
              __typename: 'File',
              key
            }
          }
        })
      },
      checkSelection (key) {
        if (!this.value) {
          return false
        } else {
          return Array.isArray(this.value)
            ? Boolean(this.value.find(item => item.key === key))
            : this.value.key === key
        }
      },
      handleSelection (newImage) {
        // if (!this.value) {
        //   return null
        // }

        const payload = pick(newImage, ['key', 'url', 'bucket'])

        if (Array.isArray(this.value)) {
          const index = this.value.findIndex(image => image.key === newImage.key)
          if (index === -1) {
            this.$emit('change', this.value.concat(payload))
          } else {
            const newValue = this.value.slice()
            newValue.splice(index, 1)
            this.$emit('change', newValue)
          }
        } else {
          this.$emit('change', this?.value?.key === newImage.key ? {} : payload)
        }
      },
      clearSelection () {
        if (!this.value) {
          return null
        }
        this.$emit('change', Array.isArray(this.value) ? [] : {})
      }
    }
  }
</script>

<style lang="sass" scoped>
  $height-footer: 50px

  .mx-2
    overflow-y: scroll
    max-height: 320px
    margin-bottom: 20px

  .uploader
    height: 100%
    overflow: hidden

  .body
    height: 100%

    &.with-footer
      height: calc(100% - #{$height-footer})
      overflow: hidden auto
      position: relative

  .footer
    align-items: center
    display: flex
    height: $height-footer
    justify-content: center

    > *:not(:last-child)
      margin-right: 10px
</style>
